/**
 * usePendingEdits — staged configuration edits for a published auction.
 *
 * Bidder-visible config changes don't write through immediately when
 * stagesEdits is true (auction is published/open). They hold here until
 * the admin clicks "Publish edits" in PendingEditsCard. The configure
 * store owns applying staged values and calling clearAll() after publish.
 *
 * Keyed by auction slug. Admin-web adaptation notes (2026-06-12 spec §2):
 * - StageableField uses draft-store keys (name, descriptionHtml, etc.),
 *   not the prototype's mock-data keys.
 * - No applyToAuction — publishing goes through the configure store here.
 * - clearAll is an alias for discardAll, used by the store after publish.
 */
import { computed, reactive } from 'vue'

/**
 * Bidder-visible draft-store keys that stage on a published/open auction.
 * NOT staged: campaignId/campaignName/crmAutoSyncEnabled (instant apply),
 * financial-locked keys (existing hasBids mechanism), timezone (deferred),
 * cover/bannerUrl (separate direct-update path, deferred).
 */
export type StageableField =
    | 'name'
    | 'descriptionHtml'
    | 'auctionOpenAt'
    | 'auctionCloseAt'
    | 'defaultIncrement'
    | 'defaultIncrementPercent'
    | 'defaultStartingBidPercent'
    | 'defaultStartingBidFixed'
    | 'proxyBidding'
    | 'termsAndConditionsEnabled'
    | 'termsAndConditionsHtml'

export interface PendingEdit {
    id: string
    field: StageableField
    /** Short field name shown on the card row, e.g. "Close time". */
    label: string
    /** Display string of the currently-published value. */
    previous: string
    /** Display string of the staged value. */
    staged: string
    /** Raw published value, used to revert the form on discard. */
    previousValue: unknown
    /** Raw staged value, written to the auction record on publish. */
    stagedValue: unknown
    timestamp: number
}

const editsByAuction = reactive<Record<string, PendingEdit[]>>({})

export function usePendingEdits(slug: string) {
    if (!editsByAuction[slug]) editsByAuction[slug] = []

    const edits = computed(() => editsByAuction[slug] ?? [])
    const hasEdits = computed(() => edits.value.length > 0)
    const editCount = computed(() => edits.value.length)

    /**
     * Stage (or re-stage) an edit. Re-editing the same field updates the
     * staged side but keeps the original published value as `previous`.
     *
     * Back-to-baseline removal is intentionally NOT done here: the caller
     * (configure store) decides what's staged from the raw-value `stagedDiff`
     * and removes non-diff entries explicitly. Doing it here on the display
     * strings would diverge from that raw diff — a raw change whose display
     * collapses (e.g. a long description edited past its 40-char preview)
     * would be filtered from the PATCH yet leave no card row.
     */
    function stageEdit(args: {
        field: StageableField
        label: string
        previous: string
        staged: string
        previousValue: unknown
        stagedValue: unknown
    }) {
        const list = editsByAuction[slug]
        const idx = list.findIndex((e) => e.field === args.field)
        const existing = idx !== -1 ? list[idx] : undefined

        if (existing) {
            existing.staged = args.staged
            existing.stagedValue = args.stagedValue
            existing.timestamp = Date.now()
        } else {
            list.push({
                id: `edit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                field: args.field,
                label: args.label,
                previous: args.previous,
                staged: args.staged,
                previousValue: args.previousValue,
                stagedValue: args.stagedValue,
                timestamp: Date.now(),
            })
        }
    }

    /** The staged raw value for a field, if an edit is pending. */
    function stagedValueFor(field: StageableField): unknown | undefined {
        return editsByAuction[slug].find((e) => e.field === field)?.stagedValue
    }

    function hasStagedEdit(field: StageableField): boolean {
        return editsByAuction[slug].some((e) => e.field === field)
    }

    function discardEdit(editId: string) {
        const idx = editsByAuction[slug].findIndex((e) => e.id === editId)
        if (idx !== -1) editsByAuction[slug].splice(idx, 1)
    }

    function discardAll() {
        editsByAuction[slug].splice(0)
    }

    /** Alias for discardAll — called by the configure store after a successful publish. */
    function clearAll() {
        editsByAuction[slug].splice(0)
    }

    return {
        edits,
        hasEdits,
        editCount,
        stageEdit,
        stagedValueFor,
        hasStagedEdit,
        discardEdit,
        discardAll,
        clearAll,
    }
}
